import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Waypoint } from 'react-waypoint';
import classnames from 'classnames';
import {
  Input,
  Table,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { APIimporterLoadFiles } from '../../api/Importers';

import useForceUpdate from '../../hooks/useForceUpdate';

import ModalBrowserListItem from './ModalBrowserListItem';
import ImporterModalError from './ModalError';

import Loading from '../Loading';
import LoadingSmall from '../LoadingSmall';

const ImporterModalBrowserList = ({
  importer,
  path,
  setPath,
  addPathElement,
  selectedItems,
  setSelectedItems,
}) => {
  const intl = useIntl();
  const forceUpdate = useForceUpdate();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [params, setParams] = useState({});
  const [page, setPage] = useState(1);
  const [ts, setTs] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (ts === null) {
      return;
    }

    params.path = path;
    if (page === 1) {
      delete params.cursor;
    }
    delete params.has_more_items;
    delete params.top_page;

    const loadFiles = () => (
      APIimporterLoadFiles(importer.service, params).then(response => (response))
    );

    setError(false);
    setLoading(true);
    loadFiles().then((response) => {
      if (page === 1) {
        setItems(response.data);
      } else {
        setItems(items.concat(response.data));
      }
      setParams({ ...params, ...response.meta });
      setLoading(false);

      // Workaround for Dropbox API returning random number of items per page
      if (importer.service === 'dropbox' && response.meta.has_more_items && items.length < 30) {
        setPage(page + 1);
        setTs(new Date());
      }
    }).catch(() => {
      setError(true);
      setLoading(false);
    });
  }, [importer.service, ts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedItems([]);
    setPage(1);
    setTs(new Date());
  }, [path, setSelectedItems]);

  const addSelectedItem = (item) => {
    if (!selectedItems.find(si => si.id === item.id)) {
      const newSelectedItems = [...selectedItems];
      newSelectedItems.push(item);
      setSelectedItems(newSelectedItems);
      forceUpdate();
    }
  };

  const removeSelectedItem = (item) => {
    setSelectedItems(selectedItems.filter(si => (si.id !== item.id)));
    forceUpdate();
  };

  const isSelectAllChecked = items.length > 0 && selectedItems.length === items.length;

  if (error) {
    return (
      <ImporterModalError importer={importer} />
    );
  }

  return (
    <div className={classnames('files-list content-wrapper', { 'top-page': params.top_page })}>
      {(loading && page === 1) ? (
        <Loading />
      ) : (
        <div className="table-wrapper">
          <Table className="files-list" responsive>
            {!params.top_page && (
              <thead>
                <tr>
                  <th className="selector">
                    <Input
                      type="checkbox"
                      className="input-check check-s form-check-input"
                      checked={isSelectAllChecked}
                      onChange={() => {
                        if (isSelectAllChecked) {
                          setSelectedItems([]);
                        } else {
                          setSelectedItems(items);
                        }
                      }}
                    />
                  </th>
                  <th>
                    {' '}
                  </th>
                  <th>
                    {intl.formatMessage({ defaultMessage: 'Name' })}
                  </th>
                  <th>
                    {intl.formatMessage({ defaultMessage: 'Extension' })}
                  </th>
                  <th>
                    {intl.formatMessage({ defaultMessage: 'Size' })}
                  </th>
                  <th>
                    {intl.formatMessage({ defaultMessage: 'Uploaded' })}
                  </th>
                </tr>
              </thead>
            )}
            <tbody>
              {items.map(item => (
                <ModalBrowserListItem
                  key={item.id}
                  item={item}
                  selectedItems={selectedItems}
                  addSelectedItem={addSelectedItem}
                  removeSelectedItem={removeSelectedItem}
                  setPath={setPath}
                  addPathElement={addPathElement}
                  topPage={!!params.top_page}
                />
              ))}
            </tbody>
          </Table>
          <Waypoint
            bottomOffset="-20px"
            onEnter={() => {
              if (params.has_more_items && !loading) {
                setPage(page + 1);
                setTs(new Date());
              }
            }}
          />
        </div>
      )}
      {(loading && page > 1) && (
        <LoadingSmall />
      )}
    </div>
  );
};

ImporterModalBrowserList.propTypes = {
  importer: PropTypes.object.isRequired,
  path: PropTypes.string.isRequired,
  setPath: PropTypes.func.isRequired,
  addPathElement: PropTypes.func.isRequired,
  selectedItems: PropTypes.array.isRequired,
  setSelectedItems: PropTypes.func.isRequired,
};

export default ImporterModalBrowserList;
